import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";

const Services = lazy(() => import("@/pages/Services"));
const Entreprises = lazy(() => import("@/pages/Entreprises"));
const Communaute = lazy(() => import("@/pages/Communaute"));
const Particuliers = lazy(() => import("@/pages/Particuliers"));
const Tarifs = lazy(() => import("@/pages/Tarifs"));
const RendezVous = lazy(() => import("@/pages/RendezVous"));
const Annulation = lazy(() => import("@/pages/Annulation"));
const Admin = lazy(() => import("@/pages/Admin"));
const About = lazy(() => import("@/pages/About"));
const Ressources = lazy(() => import("@/pages/Ressources"));
const Article = lazy(() => import("@/pages/Article"));
const Contact = lazy(() => import("@/pages/Contact"));

const ScrollManager = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    }
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const SEO_MAP = {
  "/": ["Thérapie & coaching — Centre Émergence Prudhomme", "Thérapie, coaching et accompagnement psychospirituel avec Jane Prudhomme. Consultation initiale gratuite de 20 minutes, en personne ou en visioconférence."],
  "/services": ["Nos services — Centre Émergence Prudhomme", "Trois pôles d'accompagnement : thérapie et formation pour les particuliers, consultation et formations pour les entreprises, soutien à la communauté."],
  "/particuliers": ["Services aux particuliers — Centre Émergence Prudhomme", "Accompagnement individuel : croissance personnelle, transitions de vie, identité et vocation, accompagnement psychospirituel et croissance spirituelle chrétienne."],
  "/entreprises": ["Services aux entreprises — Centre Émergence Prudhomme", "Consultante en développement organisationnel : climat de travail, médiation, leadership, formations sur mesure et coaching de gestion. Plus de 15 ans d'expérience."],
  "/communaute": ["Cœur Mission | Soutien — Centre Émergence Prudhomme", "Écoute, ligne de soutien téléphonique, visites aux personnes malades, soutien à domicile, popote roulante et dons de biens essentiels. Une présence qui réconforte."],
  "/a-propos": ["À propos de Jane Prudhomme — Centre Émergence Prudhomme", "Plus de 15 ans d'expérience et 8 années d'études universitaires. Thérapeute | Bien-être et spiritualité Chrétienne — Consultante, Coach et Formatrice."],
  "/ressources": ["Ressources & articles — Centre Émergence Prudhomme", "Articles et réflexions sur la croissance personnelle, les transitions de vie, la recherche de sens, la vocation et la spiritualité chrétienne."],
  "/contact": ["Contact & rendez-vous — Centre Émergence Prudhomme", "Contactez Jane Prudhomme : info@emergence-prudhomme.com ou +1 514 621-4802. Consultation initiale gratuite de 20 minutes, sans engagement."],
  "/tarifs": ["Tarifs — Centre Émergence Prudhomme", "Tarifs clairs : accompagnement individuel dès 150 $, coaching, forfaits avantageux, formations en entreprise et tarif solidaire. Consultation initiale gratuite."],
  "/rendez-vous": ["Prendre rendez-vous — Centre Émergence Prudhomme", "Réservez votre séance en ligne en quelques clics : service, date et heure au choix. Confirmation immédiate par courriel. Consultation initiale gratuite."],
};

const NOINDEX_PATHS = ["/admin", "/annulation"];

const SITE_URL = process.env.REACT_APP_SITE_URL || window.location.origin;

const SeoManager = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const isArticle = pathname.startsWith("/ressources/") && pathname !== "/ressources";
    if (!isArticle) {
      const [title, description] = SEO_MAP[pathname] || SEO_MAP["/"];
      document.title = title;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute("content", description);
    }
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    robots.setAttribute(
      "content",
      NOINDEX_PATHS.some((p) => pathname.startsWith(p)) ? "noindex, nofollow" : "index, follow"
    );
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `${SITE_URL}${pathname}`);
  }, [pathname]);
  return null;
};

function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <div className="App bg-cream text-deep font-sans">
      <div className="grain" aria-hidden="true" />
      <BrowserRouter>
        <ScrollManager />
        <SeoManager />
        <Navbar />
        <Suspense fallback={<div className="min-h-screen" aria-hidden="true" />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<Services />} />
            <Route path="/entreprises" element={<Entreprises />} />
            <Route path="/communaute" element={<Communaute />} />
            <Route path="/particuliers" element={<Particuliers />} />
            <Route path="/tarifs" element={<Tarifs />} />
            <Route path="/rendez-vous" element={<RendezVous />} />
            <Route path="/annulation/:token" element={<Annulation />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/a-propos" element={<About />} />
            <Route path="/ressources" element={<Ressources />} />
            <Route path="/ressources/:slug" element={<Article />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="*" element={<Home />} />
          </Routes>
        </Suspense>
        <Footer />
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
